import { memo, useCallback, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  FormControl,
  IconButton,
  SxProps,
  TextField,
  TextFieldProps,
} from "@mui/material";

const styles: Record<string, SxProps> = {
  root: {
    position: "relative",
    justifyContent: "center",
    ml: 1.5,
    width: "100%",
    maxWidth: 460,
  },

  button: {
    position: "absolute",
    zIndex: 1,
  },

  clearButton: {
    position: "absolute",
    right: 0,
    zIndex: 1,
  },

  input: {
    width: "100%",
    pl: 3,

    "& .MuiOutlinedInput-root": {
      width: "100%",
      "& input": { pl: 3, py: 1.5, pr: 5 },
      "& fieldset": { border: 0 },
      "&.Mui-focused fieldset": {
        border: 0,
        borderColor: "secondary.main",
      },
    },
  },
};

export interface SearchBarProps
  extends Omit<TextFieldProps, "onChange" | "onSubmit"> {
  defaultValue?: string;
  onChange?: (inputs: string) => void;
  onSubmit?: (inputs: string) => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  defaultValue,
  onChange,
  onSubmit,
  variant,
  sx,
  ...props
}) => {
  const [input, setInput] = useState(defaultValue ?? "");

  const handleChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
      setInput(value);
      onChange?.(value);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onSubmit?.(input);
      }
    },
    [input, onSubmit],
  );

  const handleClear = useCallback(() => {
    setInput("");
    onChange?.("");
    onSubmit?.("");
  }, [onChange, onSubmit]);

  return (
    <FormControl fullWidth sx={styles.root}>
      <IconButton aria-label="search" sx={styles.button} type="submit">
        <SearchIcon />
      </IconButton>

      {input && (
        <IconButton
          aria-label="clear search"
          sx={styles.clearButton}
          onClick={handleClear}
        >
          <CloseIcon />
        </IconButton>
      )}

      <TextField
        fullWidth
        sx={{ ...sx, ...styles.input } as SxProps}
        value={input}
        variant={variant ?? "outlined"}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        {...props}
      />
    </FormControl>
  );
};

export default memo(SearchBar);
